import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplate, FormTemplateSection,
} from 'data/template/template.types';
import * as types from 'data/radiusParserPassword/radiusParserPassword.types';
import Form, {
  FieldUpdateHandlerParam,
} from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import './radius-parser-password-form.scss';

type Props = {
  template: FormTemplate,
  data: Partial<types.RadiusParserPassword>
  i18nKeyPrefix?: string
  isEditing?: boolean,
  onFieldUpdate?: (param: FieldUpdateHandlerParam) => void
  onFieldCreateNew?: any
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.RadiusParserPassword
}

export const RADIUS_PARSER_PASSWORD_I18N_KEY = 'radiusParserPassword.form';

export const RadiusParserPasswordForm = forwardRef<Ref, Props>(({
  template,
  data,
  isEditing,
  i18nKeyPrefix = RADIUS_PARSER_PASSWORD_I18N_KEY,
  onFieldUpdate = () => null,
  onFieldCreateNew,
}: Props, ref) => {
  const { t } = useTranslation();
  const mainRef = useRef<Form>(null);
  const sipTimerRef = useRef<Form>(null);
  const sipPrefixListRef = useRef<Form>(null);
  const sipNetElementsRef = useRef<Form>(null);

  const isValid = (forceValidation = false, forceTouched = true) => {
    const ismainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSipTimerValid = sipTimerRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSipPrefixListValid = sipPrefixListRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSipNetElementsValid = sipNetElementsRef.current?.isValid(forceValidation, forceTouched) ?? true;

    return ismainValid && isSipTimerValid && isSipPrefixListValid && isSipNetElementsValid;
  };

  const getValues = () => {
    return mainRef.current?.getValues();
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const onMainFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param);

  return (
    <div className="radius-parser-password-form" data-testid="RadiusParserPasswordForm">
      <div data-testid="RadiusParserPasswordFormMain">
        <FormLayout>
          <Form
            ref={mainRef}
            name={template.formId ?? template.key}
            defaults={data}
            fields={(template.main as FormTemplateSection).fields}
            layout={(template.main as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={onMainFieldUpdate}
            fieldCreateNewHandler={onFieldCreateNew}
          />
        </FormLayout>
      </div>
    </div>
  );
});

export default RadiusParserPasswordForm;
